/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* ======================================================================================================================
 * REWARDS LADDER
 *
 * Keeps <rewards-ladder> (snippets/rewards-ladder.liquid) in step with the cart, and handles picking a gift.
 *
 * Why any JavaScript at all: the snippet is server-rendered and correct on load, and the cart drawer re-renders itself
 * from Liquid on "cart:refresh" — but that re-render is deliberately delayed 600-750ms by the theme, and on the product
 * page it never happens at all. Without this the ladder would sit stale under the add-to-cart button, which is the one
 * place it most needs to react.
 *
 * The arithmetic below mirrors the Liquid in the snippet. That duplication is deliberate and must stay in sync: if you
 * change how the qualifying total is computed in one, change it in the other. The COPY is not duplicated — the message
 * templates are rendered once by Liquid into data attributes, so the translations live only in the locale files.
 * ====================================================================================================================== */

(function () {
  var CLONE_SUFFIX = '-sca_clone_freegift';

  // The shop's own money format, handed over from Liquid, so that an amount this script writes cannot look different
  // from the one the server rendered. Intl's currency style produced "59,90 €" while Shopify's `money` filter gives
  // "€59,90" here, and the symbol visibly jumped sides the moment the cart changed.
  function formatMoney(cents, format) {
    var value = cents / 100;

    function grouped(thousands, decimal) {
      var parts = value.toFixed(2).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + decimal + parts[1];
    }

    return String(format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      if (name === 'amount_with_comma_separator') return grouped('.', ',');
      if (name === 'amount_no_decimals') return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (name === 'amount_no_decimals_with_comma_separator') {
        return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      }
      return grouped(',', '.');
    });
  }

  function isGiftLine(item) {
    return typeof item.handle === 'string' && item.handle.indexOf(CLONE_SUFFIX) !== -1;
  }

  /* --------------------------------------------------------------------------------------------------------------
   * BOGOS
   *
   * BOGOS, not this widget, grants the gift. Observed on the live shop: adding a gift clone with a plain
   * /cart/add.js puts it in the cart and BOGOS removes it again within a few seconds, via
   * POST /cart/update.js?sca_process_gifts.
   *
   * Reading its own addGiftToCart(variantId, quantity, offerId) shows why. Before touching the cart it records the
   * line in FGSECOMAPP.arrOfferAdded as {variant_id, belongs_to_offer, quantity, code}, persists that to local
   * storage, and only then adds through /cart/add.js?sca_add_gift. A gift added any other way is not registered
   * against an offer, so when BOGOS reconciles the cart it rightly treats it as unauthorised and strips it.
   *
   * So the widget asks BOGOS to do it. The buttons stay — they are the chooser — but the gift itself is BOGOS's,
   * and after every change the widget redraws from the cart BOGOS has settled on rather than from its own guess.
   * -------------------------------------------------------------------------------------------------------------- */

  // The moments BOGOS says it has finished reconciling. Redrawing on these is what keeps the widget honest: what it
  // shows is then what the shopper will actually be charged for, even if they used BOGOS's own slider instead.
  var BOGOS_SETTLED_EVENTS = ['fg-cart:end-process', 'fg-gifts:updated', 'fg-gifts:added', 'bogos:cart-change'];

  function bogosApp() {
    var app = window.FGSECOMAPP;
    return app && typeof app === 'object' ? app : null;
  }

  function bogosCanAdd() {
    var app = bogosApp();
    return !!(app && app.helper && typeof app.helper.addGiftToCart === 'function');
  }

  // Which running offer grants this variant. The shape of an offer's gift list is not documented and cannot be read
  // while no offer is running, so rather than assume a path this walks the offer and looks for the id. A Shopify
  // variant id is a 14-digit number, so a coincidental match on some unrelated field is not a real risk.
  function offerIdFor(variantId) {
    var app = bogosApp();
    var offers = app && app.offers;
    if (!offers || !offers.length) return null;

    function holds(node, depth, seen) {
      if (node == null || depth > 6) return false;
      var type = typeof node;
      if (type === 'number' || type === 'string') return parseInt(node, 10) === variantId;
      if (type !== 'object' || seen.indexOf(node) !== -1) return false;
      seen.push(node);
      for (var key in node) {
        if (!Object.prototype.hasOwnProperty.call(node, key)) continue;
        if (holds(node[key], depth + 1, seen)) return true;
      }
      return false;
    }

    for (var i = 0; i < offers.length; i++) {
      if (holds(offers[i], 0, [])) return offers[i].id;
    }
    return null;
  }

  // Drop a gift the shopper is replacing, and take it out of BOGOS's own register too — otherwise BOGOS still
  // believes it granted that line and can put it back.
  async function forgetGift(line, changeUrl) {
    await fetch(changeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });

    try {
      var app = bogosApp();
      if (!app || !Array.isArray(app.arrOfferAdded)) return;
      app.arrOfferAdded = app.arrOfferAdded.filter(function (entry) {
        return parseInt(entry && entry.variant_id, 10) !== line.variant_id;
      });
      var key = app.variables && app.variables.constants && app.variables.constants.sca_offers_added;
      if (key && window.localStorage) {
        window.localStorage.setItem(key, JSON.stringify(app.arrOfferAdded));
      }
    } catch (e) {
      // Its bookkeeping is internal and may change; failing to prune it is not worth breaking the pick over.
    }
  }

  function cleanGiftName(title) {
    return String(title || '')
      .replace('(100% off)', '')
      .replace('🎁', '')
      .trim();
  }

  var RewardsLadder = class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onBogosSettled = this._onBogosSettled.bind(this);
      document.documentElement.addEventListener('cart:updated', this._onCartUpdated);
      this.addEventListener('click', this._onClick);
      // Also follow BOGOS, so the widget stays right even when the shopper takes the gift from BOGOS's own slider
      // instead of from here, and when BOGOS changes a gift on its own.
      BOGOS_SETTLED_EVENTS.forEach(function (name) {
        document.addEventListener(name, self._onBogosSettled);
        window.addEventListener(name, self._onBogosSettled);
      });
    }

    // The theme's own FreeShippingBar never removes its listener, so a long session accumulates dead handlers on
    // detached nodes. Not repeating that here: the drawer replaces this element on every cart refresh.
    disconnectedCallback() {
      var self = this;
      clearTimeout(this._settleTimer);
      document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
      BOGOS_SETTLED_EVENTS.forEach(function (name) {
        document.removeEventListener(name, self._onBogosSettled);
        window.removeEventListener(name, self._onBogosSettled);
      });
    }

    // BOGOS fires several of these in a burst while it reconciles, and some of them land on both document and
    // window. Redraw once, after the burst.
    _onBogosSettled() {
      var self = this;
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(function () {
        self._resync(false);
      }, 150);
    }

    // Everything shown comes from a fresh read of the cart, so whatever BOGOS settled on — including a gift other
    // than the one that was clicked — is what the shopper sees.
    async _resync(announce) {
      try {
        var routes = (window.themeVariables && window.themeVariables.routes) || {};
        var cart = await (await fetch((routes.cartUrl || '/cart') + '.js', { cache: 'no-store' })).json();
        this.render(cart);
        if (announce) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } })
          );
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } })
          );
        }
      } catch (e) {
        // A failed read leaves the last good render standing, which beats blanking the widget.
      }
    }

    get giftThreshold() {
      return parseInt(this.dataset.giftThreshold, 10) || 0;
    }

    get shippingThreshold() {
      return parseInt(this.dataset.shippingThreshold, 10) || 0;
    }

    /* Mirrors the Liquid: everything that is not a gift clone, minus cart-level discounts, never below zero. */
    _qualifyingTotal(cart) {
      var paid = (cart.items || [])
        .filter(function (item) {
          return !isGiftLine(item);
        })
        .reduce(function (sum, item) {
          return sum + item.final_line_price;
        }, 0);
      var discounts = (cart.cart_level_discount_applications || []).reduce(function (sum, d) {
        return sum + (d.total_allocated_amount || 0);
      }, 0);
      return Math.max(0, paid - discounts);
    }

    _onCartUpdated(event) {
      var cart = event && event.detail && event.detail.cart;
      if (!cart) return;
      this.render(cart);
    }

    render(cart) {
      var self = this;
      var total = this._qualifyingTotal(cart);
      var gift = this.giftThreshold;
      var shipping = this.shippingThreshold;
      var giftLine = (cart.items || []).filter(isGiftLine)[0];

      var track = this.querySelector('[data-rewards-track]');
      if (track && gift > 0) track.style.setProperty('--progress', Math.min(total / gift, 1));

      // Both the tick on the bar and the matching row in the legend carry the same marker, so toggle every match.
      function mark(name, reached) {
        var els = self.querySelectorAll('[data-rewards-milestone="' + name + '"]');
        for (var i = 0; i < els.length; i++) els[i].classList.toggle('is-reached', reached);
      }
      mark('shipping', shipping > 0 && total >= shipping);
      mark('gift', total >= gift);

      var locked = total < gift;
      var options = this.querySelector('[data-rewards-options]');
      if (options) options.classList.toggle('is-locked', locked);

      // Mirrors `ladder_state` in the snippet. In the drawer it decides how much of the widget is shown (custom.css).
      // A change of state folds the cards away: once a gift has been picked, the widget shrinks to the one line that
      // names it, without the shopper having to close anything.
      var state = giftLine ? (locked ? 'at-risk' : 'chosen') : locked ? 'locked' : 'unlocked';
      if (this.getAttribute('data-state') !== state) {
        this.setAttribute('data-state', state);
        this._setExpanded(false);
        this._flavoursFor = null;
      }

      // The card of the gift in the cart, matched on the clone's handle (data-rewards-gift) as the snippet does: a
      // clone with more than one variant may hold a variant other than the one on the card's button.
      var chosenCard = null;
      if (giftLine) {
        var cards = this.querySelectorAll('[data-rewards-gift]');
        for (var c = 0; c < cards.length; c++) {
          if (cards[c].getAttribute('data-rewards-gift') === giftLine.handle) chosenCard = cards[c];
        }
      }

      // Liquid renders a button on every card in every state and leaves it disabled; enabling them is this method's
      // job. The product page never re-renders from Liquid, so without this a shopper who crossed the threshold right
      // there would be left looking at four dead cards — which is exactly how it used to behave.
      var chosenId = giftLine ? giftLine.variant_id : 0;
      // The card buttons: a variant to pick (data-rewards-pick) or, for a gift with flavours, the row to open
      // (data-rewards-open). The flavour buttons also carry data-rewards-pick but are not cards: they keep their own
      // label and are handled by _syncFlavours below.
      var buttons = this.querySelectorAll(
        '.rewards__option-button[data-rewards-pick], .rewards__option-button[data-rewards-open]'
      );
      for (var i = 0; i < buttons.length; i++) {
        var button = buttons[i];
        var mine =
          (chosenCard !== null && button.closest('.rewards__option') === chosenCard) ||
          parseInt(button.getAttribute('data-rewards-pick'), 10) === chosenId;
        button.disabled = locked;
        button.setAttribute('aria-pressed', mine ? 'true' : 'false');
        button.textContent = mine ? this.dataset.labelChosen || '' : this.dataset.labelChoose || '';
        var card = button.closest('.rewards__option');
        if (card) card.classList.toggle('is-chosen', mine);
      }

      // null, not undefined, when the cart holds no gift: undefined is how _syncFlavours tells a tap (cart unknown)
      // from this call, and taking "no gift" for a tap left every flavour as Liquid printed it. On a product page
      // loaded below the threshold that meant four disabled flavours after the cart crossed it.
      this._syncFlavours(giftLine || null, locked);

      var status = this.querySelector('[data-rewards-status]');
      if (!status) return;

      // Message templates are rendered by Liquid into data attributes, already translated, with @amount@ to fill in.
      function fill(key, cents) {
        return String(self.dataset[key] || '').replace('@amount@', formatMoney(cents, self.dataset.moneyFormat));
      }

      if (giftLine && total < gift) {
        // The dangerous state: a gift is in the cart but the cart no longer qualifies for it. Say so plainly rather
        // than reassuring the shopper, because at checkout they would simply be charged for it.
        status.innerHTML = fill('msgAtRisk', gift - total);
      } else if (giftLine) {
        // The real product's name, as printed on its card by Liquid (snippets/bogos-gift-title.liquid): /cart.js only
        // knows the clone's title, which BOGOS writes in capitals for some gifts.
        var chosenTitle = chosenCard && chosenCard.querySelector('.rewards__option-title');
        var giftName = chosenTitle ? chosenTitle.textContent.trim() : cleanGiftName(giftLine.product_title);
        // The flavour too, as the snippet does: "ISOZERO BAR bioextreme protein Volactive (Cacao)".
        if (giftLine.variant_title && !giftLine.product_has_only_default_variant) {
          giftName += ' (' + giftLine.variant_title + ')';
        }
        // Escaped, because the template is HTML; and passed through a function so that a "$" in a name stays literal.
        var giftHtml = giftName.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        status.innerHTML = String(this.dataset.msgChosen || '').replace('@gift@', function () {
          return giftHtml;
        });
      } else if (total >= gift) {
        status.innerHTML = this.dataset.msgUnlocked || '';
      } else if (shipping > 0 && total >= shipping) {
        status.innerHTML = fill('msgGiftNextAfterShipping', gift - total);
      } else if (shipping > 0) {
        status.innerHTML = fill('msgShippingNext', shipping - total);
      } else {
        status.innerHTML = fill('msgGiftNext', gift - total);
      }
    }

    // The flavour rows (snippets/rewards-ladder.liquid prints one per gift with flavours, after the cards). The row of
    // the chosen gift is open, so its flavour can be changed; so is the row of a card the shopper has just tapped
    // (this._flavoursFor). Called by render() with the gift line (null for none), and by a tap on a card with no
    // argument, which only opens or closes rows. One row at most is open: the tapped one, else the chosen one.
    _syncFlavours(giftLine, locked) {
      var chosenCard = this.querySelector('.rewards__option.is-chosen[data-rewards-gift]');
      var chosenHandle = chosenCard ? chosenCard.getAttribute('data-rewards-gift') : null;
      var panels = this.querySelectorAll('[data-rewards-flavours]');

      for (var p = 0; p < panels.length; p++) {
        var panel = panels[p];
        var handle = panel.getAttribute('data-rewards-flavours');
        var open = handle === (this._flavoursFor || chosenHandle);
        panel.classList.toggle('is-open', open);

        var opener = this.querySelector('[data-rewards-open="' + handle + '"]');
        if (opener) {
          opener.setAttribute('aria-expanded', open ? 'true' : 'false');
          opener.closest('.rewards__option').classList.toggle('is-pending', handle === this._flavoursFor && handle !== chosenHandle);
        }

        // Only from render(), which knows the cart.
        if (giftLine === undefined) continue;

        // A flavour the running BOGOS offer does not list cannot be granted: BOGOS would strip it again. It is greyed
        // out only when the offer lists some of this gift's flavours by variant and not this one; if it lists none
        // of them, the offer's shape is not what this expects and every flavour is left to try.
        var flavours = panel.querySelectorAll('[data-rewards-flavour]');
        var listed = [];
        var anyListed = false;
        for (var f = 0; f < flavours.length; f++) {
          listed[f] = offerIdFor(parseInt(flavours[f].getAttribute('data-rewards-pick'), 10)) !== null;
          anyListed = anyListed || listed[f];
        }

        for (var g = 0; g < flavours.length; g++) {
          var flavour = flavours[g];
          var id = parseInt(flavour.getAttribute('data-rewards-pick'), 10);
          flavour.setAttribute('aria-pressed', giftLine && giftLine.variant_id === id ? 'true' : 'false');
          flavour.disabled = locked || flavour.getAttribute('data-available') === 'false' || (anyListed && !listed[g]);
        }
      }
    }

    // The drawer's "Vedi omaggi" / "Cambia" button. The open state lives on the element as data-expanded, which the
    // drawer CSS reads; the drawer re-renders on every cart refresh, so the cards start folded again after a change.
    _setExpanded(open) {
      var toggle = this.querySelector('[data-rewards-toggle]');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        this.setAttribute('data-expanded', '');
      } else {
        this.removeAttribute('data-expanded');
      }
    }

    async _onClick(event) {
      var toggle = event.target.closest('[data-rewards-toggle]');
      if (toggle) {
        this._setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      // A gift with flavours: its card opens (or closes) the row of flavours instead of adding anything.
      var opener = event.target.closest('[data-rewards-open]');
      if (opener) {
        var handle = opener.getAttribute('data-rewards-open');
        this._flavoursFor = this._flavoursFor === handle ? null : handle;
        this._syncFlavours();
        return;
      }

      var button = event.target.closest('[data-rewards-pick]');
      if (!button || button.hasAttribute('aria-busy')) return;
      event.preventDefault();

      var variantId = parseInt(button.getAttribute('data-rewards-pick'), 10);
      if (!variantId) return;

      // One pick at a time for the whole widget, not per button. Each pick reads the cart, removes the gift it finds
      // and adds its own; two quick taps on different flavours both read the cart before either had changed it, and
      // the order ended up with two gifts. A tap during a pick is dropped; the shopper can tap again once it settles.
      if (this._picking) return;
      this._picking = true;

      button.setAttribute('aria-busy', 'true');

      try {
        var routes = (window.themeVariables && window.themeVariables.routes) || {};
        var cartUrl = routes.cartUrl || '/cart';
        var changeUrl = routes.cartChangeUrl || '/cart/change';
        var addUrl = routes.cartAddUrl || '/cart/add';

        // Read the cart fresh rather than trusting the rendered state: it may have changed in another tab.
        var current = await (await fetch(cartUrl + '.js', { cache: 'no-store' })).json();
        var existing = (current.items || []).filter(isGiftLine)[0];

        // Already the chosen one: the same test as render(), by variant or by the clone's handle. By variant alone, a
        // gift taken in another flavour (ISOZERO BAR has four, and the card's button carries only the first) was
        // removed and replaced with that first flavour when the shopper tapped its "Scelto" button after "Cambia".
        var card = button.closest('.rewards__option[data-rewards-gift]');
        var sameGift =
          existing &&
          (existing.variant_id === variantId || (card !== null && card.getAttribute('data-rewards-gift') === existing.handle));
        if (sameGift) {
          this._setExpanded(false);
          return;
        }

        // One gift per order, so swapping means letting go of the old one first — in BOGOS's register as well.
        if (existing) {
          await forgetGift(existing, changeUrl);
        }

        // Ask BOGOS to grant it. This is the whole point: going through its own addGiftToCart registers the line
        // against the offer, so BOGOS keeps the gift instead of stripping it on the next reconciliation.
        var offerId = offerIdFor(variantId);
        var granted = false;
        if (bogosCanAdd() && offerId != null) {
          try {
            await window.FGSECOMAPP.helper.addGiftToCart(variantId, 1, offerId);
            granted = true;
          } catch (e) {
            granted = false;
          }
        }

        // Only when BOGOS is not running this: outside the campaign window, or if its script failed to load. Such a
        // line would not survive a BOGOS reconciliation — but with no BOGOS there is nothing to reconcile it away.
        if (!granted) {
          await fetch(addUrl + '.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });
        }

        await this._resync(true);

        // Swapping one gift for another leaves the state at "chosen", so the change of state alone would not fold
        // the cards opened with "Cambia". The pick is made: fold them.
        this._flavoursFor = null;
        this._syncFlavours();
        this._setExpanded(false);
      } catch (e) {
        // Swallowed deliberately: the shopper can always pick the gift in the cart instead, and the finally below
        // gives them the button back either way.
      } finally {
        // This has to run on success too, not only on failure. In the drawer the element is replaced on every cart
        // refresh so a stale attribute went unnoticed, but on the product page the same node lives on — a button left
        // aria-busy stays pointer-events:none, and the shopper could never change their mind.
        button.removeAttribute('aria-busy');
        this._picking = false;
      }
    }
  };

  if (!window.customElements.get('rewards-ladder')) {
    window.customElements.define('rewards-ladder', RewardsLadder);
  }

  /* --------------------------------------------------------------------------------------------------------------
   * CART DRAWER AND BOGOS
   *
   * After "Aggiungi" the drawer is redrawn (and, with cart_type "drawer", opened; with "message" it is still there
   * behind the header cart icon) from the HTML that /cart/add.js returned with sections=mini-cart: the cart at the
   * instant of the add, before BOGOS has looked at it. BOGOS then adds,
   * swaps or strips a gift line with requests of its own, and nothing tells the theme. The ladder follows (above,
   * it redraws on the same BOGOS events), but the line items under it would not: a gift still listed after BOGOS
   * removed it, or missing after BOGOS added it.
   *
   * So once BOGOS has settled, /cart.js is compared with the lines the drawer has drawn (data-line-key and
   * data-line-quantity in sections/mini-cart.liquid), and only when they differ is the theme asked for one
   * "cart:refresh", which re-renders the drawer from Liquid. Comparing first is what stops this from looping or
   * flickering: the refresh brings the drawer in line with /cart.js, so the next check finds nothing to do.
   *
   * The check runs 800ms after the last BOGOS event because the theme itself waits up to 750ms before swapping in
   * new drawer HTML (after a quantity change, or an add from inside the drawer). Checking earlier would compare
   * against a drawer that is about to be replaced anyway, and refresh it for nothing.
   *
   * Only one /cart.js read is in flight at a time. A BOGOS event that arrives meanwhile is not dropped: on a slow
   * in-app browser that read can take longer than the delay, and its answer may predate the change BOGOS just made,
   * so one more check is queued for when the current one ends.
   * -------------------------------------------------------------------------------------------------------------- */

  var DRAWER_CHECK_DELAY = 800;
  var drawerCheckTimer = null;
  var drawerCheckRunning = false;
  var drawerCheckPending = false;

  function drawnLines(drawer) {
    var items = drawer.querySelectorAll('line-item[data-line-key]');
    var lines = [];
    for (var i = 0; i < items.length; i++) {
      lines.push(items[i].getAttribute('data-line-key') + '*' + items[i].getAttribute('data-line-quantity'));
    }
    return lines.join('|');
  }

  function cartLines(cart) {
    return (cart.items || [])
      .map(function (item) {
        return item.key + '*' + item.quantity;
      })
      .join('|');
  }

  async function syncDrawerWithCart() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (drawerCheckRunning) {
      drawerCheckPending = true;
      return;
    }
    drawerCheckRunning = true;

    try {
      var routes = (window.themeVariables && window.themeVariables.routes) || {};
      var cart = await (await fetch((routes.cartUrl || '/cart') + '.js', { cache: 'no-store' })).json();
      if (cartLines(cart) === drawnLines(drawer)) return;

      // No "sections" in the detail on purpose: the drawer then fetches its own fresh render. The cart is passed
      // only so the header counter does not need a request of its own.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
    } catch (e) {
      // A failed read leaves the drawer as it is; the next change to the cart redraws it anyway.
    } finally {
      drawerCheckRunning = false;

      // Queued through the same delay, so a refresh this check may just have asked for has time to land first.
      if (drawerCheckPending) {
        drawerCheckPending = false;
        scheduleDrawerCheck();
      }
    }
  }

  function scheduleDrawerCheck() {
    clearTimeout(drawerCheckTimer);
    drawerCheckTimer = setTimeout(syncDrawerWithCart, DRAWER_CHECK_DELAY);
  }

  BOGOS_SETTLED_EVENTS.forEach(function (name) {
    document.addEventListener(name, scheduleDrawerCheck);
    window.addEventListener(name, scheduleDrawerCheck);
  });
})();

/* ------------------------------------------------------------------------------------------------------------------
 * SLIDESHOW CAMPAIGN - "Copy the code" button
 *
 * Markup in snippets/slideshow-campaign.liquid. One delegated listener, so it keeps working when the theme editor
 * re-renders the section, with no element to re-bind.
 *
 * The code is also printed as selectable text next to the button, so if every copy method fails the shopper can still
 * long-press it. The Clipboard API is tried first; the textarea + execCommand path is for the in-app browsers of
 * Instagram and Facebook (about half of this shop's sessions), where the Clipboard API is not always exposed.
 * ------------------------------------------------------------------------------------------------------------------ */
(() => {
  const copyWithTextarea = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (e) {
      copied = false;
    }

    textarea.remove();
    return copied;
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) {
        // Fall through: some webviews expose the API but reject the call.
      }
    }

    return copyWithTextarea(text);
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-campaign-copy]');

    if (!button) {
      return;
    }

    event.preventDefault();

    const copied = await copyText(button.getAttribute('data-campaign-copy'));
    const status = button.parentElement.querySelector('[data-campaign-copy-status]');

    if (!copied) {
      // Nothing was copied, so say nothing was: select the visible code instead, ready for a long-press.
      const value = button.parentElement.querySelector('.slideshow-campaign__code-value');

      if (value) {
        const range = document.createRange();
        range.selectNodeContents(value);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }

      return;
    }

    if (!button.hasAttribute('data-label-idle')) {
      button.setAttribute('data-label-idle', button.textContent);
    }

    button.textContent = button.getAttribute('data-label-done');
    button.classList.add('is-copied');

    if (status) {
      status.textContent = button.getAttribute('data-label-done');
    }

    clearTimeout(button._campaignCopyTimer);
    button._campaignCopyTimer = setTimeout(() => {
      button.textContent = button.getAttribute('data-label-idle');
      button.classList.remove('is-copied');

      if (status) {
        status.textContent = '';
      }
    }, 2000);
  });
})();

/* ------------------------------------------------------------------------------------------------------------------
 * CATEGORY CHIPS - start the row at the current chip
 *
 * Markup in snippets/collection-subnav.liquid (and sections/category-chips.liquid). On a subcategory page the row lists
 * the sibling categories with the current one marked aria-current. Integrazione has 18 of them and a phone shows the
 * first three or four, so on "Cura dei capelli, denti e unghie" the marked chip would start off screen at the far end
 * and the row would not show where the shopper is. This scrolls the ROW, once, so that chip is centred; it never calls
 * scrollIntoView, which could also scroll the page (a reload or a back navigation can restore the page lower down).
 * A chip that is already fully visible is left alone, and there is no snapping: the shopper scrolls freely afterwards.
 * ------------------------------------------------------------------------------------------------------------------ */
(() => {
  const showCurrentChip = (root) => {
    root.querySelectorAll('.category-chips__scroller').forEach((scroller) => {
      const chip = scroller.querySelector('[aria-current="page"]');

      if (!chip || scroller.scrollWidth <= scroller.clientWidth) {
        return;
      }

      const rowBox = scroller.getBoundingClientRect();
      const chipBox = chip.getBoundingClientRect();

      if (chipBox.left >= rowBox.left && chipBox.right <= rowBox.right) {
        return;
      }

      // Physical offsets, so the arithmetic holds whatever the writing direction.
      scroller.scrollLeft += chipBox.left - rowBox.left - (rowBox.width - chipBox.width) / 2;
    });
  };

  // The script is deferred: the markup is already parsed when it runs. Then once more when the web fonts are in:
  // chips measured in the fallback font are narrower, and on the preview the last of the 18 chips ended up half off
  // screen after Montserrat swapped in. The second pass does nothing if the chip is already fully visible.
  showCurrentChip(document);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => showCurrentChip(document));
  }

  // The theme editor re-renders a section in place, without a page load.
  document.addEventListener('shopify:section:load', (event) => showCurrentChip(event.target));
})();

/* ------------------------------------------------------------------------------------------------------------------
 * CLARITY - add to cart, begin checkout and in-app browser
 *
 * Clarity records page views but does not know when a product goes into the cart, and it cannot follow the shopper
 * into the checkout, so its recordings could not be filtered by those two moments. This sends both as custom events,
 * and tags whether the page is open inside Instagram's or Facebook's in-app browser so those visits can be filtered too.
 *
 * This module never loads Clarity and never sets a cookie: it only queues calls on Clarity's standard stub. Loading
 * Clarity and handling consent stay with the tag manager, the cookie banner and Clarity's own settings. If Clarity is
 * not on the page, the queued calls do nothing.
 *
 * Clarity only. Elevar already sends the add to cart (dl_add_to_cart in window.dataLayer) and the checkout steps to
 * GA4, so nothing here touches dataLayer: a second push would double-count them there. For the same reason, do not
 * also add a GTM tag that sends add_to_cart to Clarity on dl_add_to_cart, or Clarity would get every add twice.
 * ------------------------------------------------------------------------------------------------------------------ */
(() => {
  // Clarity's own stub. GTM's template reuses an existing window.clarity instead of replacing it, and Clarity replays
  // clarity.q once it loads, so calls made before then are kept. If it never loads, they sit in memory and do nothing.
  window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  // Looked up on every call, because Clarity swaps in its real function once it has loaded.
  const clarity = (...args) => {
    try {
      window.clarity(...args);
    } catch (e) {
      // Analytics must never be the reason a cart or checkout button stops working.
    }
  };

  // Instagram's user agent carries "Instagram"; Facebook's and Messenger's carry FBAN (iOS) and/or FBAV. Instagram is
  // tested first so that it is never filed under Facebook. Set on every page rather than once: without consent Clarity
  // runs cookieless and cannot tie page views together, so a tag set on the landing page alone would be missing from
  // every other page of the visit.
  const userAgent = navigator.userAgent || '';
  let inAppBrowser = 'no';

  if (/Instagram/i.test(userAgent)) {
    inAppBrowser = 'instagram';
  } else if (/FBAN|FBAV/i.test(userAgent)) {
    inAppBrowser = 'facebook';
  }

  clarity('set', 'in_app_browser', inAppBrowser);

  // The theme fires variant:added after every successful add from a product form: product page, sticky bar, quick buy,
  // cart recommendations. It is not fired on the cart page, where the theme reloads the page instead, nor for the gift
  // picked in the rewards ladder, which BOGOS adds and which is not a purchase decision.
  document.addEventListener('variant:added', () => clarity('event', 'add_to_cart'));

  /* Begin checkout. Every way to the checkout this theme has:
   * - The cart forms (sections/mini-cart.liquid, sections/main-cart.liquid) post to /cart with a hidden
   *   name="checkout", which Shopify answers with a redirect to the checkout, so any submit of them is a start: the
   *   button, or Enter in a quantity field. The click on a button named "checkout" is caught as well, because a
   *   script that calls form.submit() (as the theme's loader-button does on Safari) leaves no submit event behind.
   * - Express wallets and "Buy it now": Shopify's own elements, with closed shadow roots, so a tap on any button inside
   *   reaches this document as a click on the element itself. Shop Pay and PayPal sit in iframes, handled further down.
   * - Links to /checkout, which apps sometimes print.
   * Counted once per page view: one tap on the drawer button is both a click and a submit, and a shopper who closes the
   * Apple Pay sheet and then taps the regular button has begun checking out once. */
  const EXPRESS_CHECKOUT = [
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    'shopify-buy-it-now-button',
    '.shopify-payment-button__button',
  ].join(', ');
  const CHECKOUT_PATH = /^(\/[a-z]{2}(-[a-z]{2,4})?)?\/checkouts?(\/|$)/i;
  let checkoutTagged = false;

  const beginCheckout = () => {
    if (checkoutTagged) {
      return;
    }

    checkoutTagged = true;
    clarity('event', 'begin_checkout');
  };

  const isCheckoutUrl = (url) => {
    if (!url) {
      return false;
    }

    try {
      // Same origin only: this shop's checkout lives on its own domain (/checkouts/...), while a link from a blog post
      // or an app to another site's /checkout is not a start here.
      const parsed = new URL(url, window.location.href);

      return parsed.origin === window.location.origin && CHECKOUT_PATH.test(parsed.pathname);
    } catch (e) {
      return false;
    }
  };

  // Capture phase for both: these only listen, and a handler further down that stops propagation must not hide a start.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (
        form instanceof HTMLFormElement &&
        (form.querySelector('[name="checkout"]') || isCheckoutUrl(form.getAttribute('action')))
      ) {
        beginCheckout();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest(`button[name="checkout"], input[type="submit"][name="checkout"], ${EXPRESS_CHECKOUT}`)) {
        beginCheckout();
        return;
      }

      const link = target.closest('a[href]');

      if (link && isCheckoutUrl(link.getAttribute('href'))) {
        beginCheckout();
      }
    },
    true
  );

  // A tap inside an iframe (Shop Pay, PayPal) never reaches this document as a click. What this window does see is its
  // own focus leaving for that iframe, which leaves a wallet element as the active element (the closed shadow root
  // hides which part of it). Best effort: it relies on the browser moving the focus into the iframe on tap.
  window.addEventListener('blur', () => {
    // Read on the next task, once the focus has actually moved.
    setTimeout(() => {
      const focused = document.activeElement;

      if (focused && focused.closest && focused.closest(EXPRESS_CHECKOUT)) {
        beginCheckout();
      }
    }, 0);
  });

  // Back from the checkout, the browser can restore this page from its cache with the flag still set. For the shopper
  // that is a new visit to the page, and a second start from it should be counted.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      checkoutTagged = false;
    }
  });
})();
